// ─── Account Query & Mutation Hooks ─────────────────────────────────
// React Query hooks for account data.
// These replace the useEffect+useState pattern in AccountContext.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchAccount,
  updateAccountAPI,
  deleteAccountAPI,
  fetchCollectibles,
  fetchCollectibleSvgs,
  type UserProfile,
} from '@/lib/api/account';

/** Fetch the current user's profile. Enabled only when a token is available. */
export function useUserProfile(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.account.me,
    queryFn: () => fetchAccount(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 min — rarely changes
  });
}

/** Fetch the user's collectibles + their SVG previews. */
export function useCollectibles(token: string | undefined) {
  const collectiblesQuery = useQuery({
    queryKey: queryKeys.account.collectibles,
    queryFn: () => fetchCollectibles(token!),
    enabled: !!token,
    staleTime: 30 * 60 * 1000, // 30 min — very rarely changes
  });

  // Derive SVGs from collectibles (only when they exist)
  const collectibles = collectiblesQuery.data ?? [];
  const svgsQuery = useQuery({
    queryKey: [...queryKeys.account.collectibles, 'svgs'],
    queryFn: () => fetchCollectibleSvgs(collectibles),
    enabled: collectibles.length > 0,
    staleTime: 30 * 60 * 1000,
  });

  return {
    collectibles: collectiblesQuery.data ?? [],
    collectibleSvgs: svgsQuery.data ?? [],
    isLoading: collectiblesQuery.isLoading || (collectibles.length > 0 && svgsQuery.isLoading),
    error: collectiblesQuery.error || svgsQuery.error,
    refetch: collectiblesQuery.refetch,
  };
}

/** Update the user's profile and invalidate the cache. */
export function useUpdateAccount(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<Pick<UserProfile, 'name' | 'image'>>) =>
      updateAccountAPI(token!, updates),
    onSuccess: (data) => {
      // Optimistically update the cache with the new profile
      queryClient.setQueryData(queryKeys.account.me, data);
    },
  });
}

/** Delete the user's account. */
export function useDeleteAccount(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteAccountAPI(token!),
    onSuccess: () => {
      // Clear all account-related caches
      queryClient.removeQueries({ queryKey: queryKeys.account.me });
      queryClient.removeQueries({ queryKey: queryKeys.account.collectibles });
    },
  });
}
/** Set the user's tail tag (unique handle for pack/family invites). */
export function useSetTailTag(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tailTag: string) => {
      const resp = await fetch(`${API_URL}/account/tail-tag`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tail_tag: tailTag }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Failed to set tail tag');
      }
      qc.invalidateQueries({ queryKey: queryKeys.account.me });
      return resp.json();
    },
  });
}
